'use client';

import { Stack, Typography } from '@mui/material';
import type { SparkEngine } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface DeleteSparkEngineDialogProps {
  engine: SparkEngine;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteSparkEngineDialog = ({
  engine,
  workspaceId,
  open,
  onClose,
  onDeleted,
}: DeleteSparkEngineDialogProps) => {
  const dal = useDAL();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await dal.lakehouse.deleteSparkEngine(workspaceId, engine.id);
      onDeleted();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete Spark engine'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Delete Spark Engine',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Delete',
          onClick: handleDelete,
          'data-testid': 'confirm-delete-spark-engine-btn',
          disabled: isLoading,
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-delete-spark-engine-btn',
        },
      }}
    >
      <Stack gap='16px' padding='0 40px'>
        <Typography>
          Are you sure you want to delete the Spark engine{' '}
          <strong>{engine.name}</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone.
        </Typography>
        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
